import * as React from "react";
import pluralize from "pluralize";
import { ArrowRight } from "lucide-react";
import { Button, Field, Skeleton } from "@/components/ui";
import { HoloBorder } from "@/components/Holo";
import { Media } from "@/interfaces";
import { DesignPackageDetail } from "@/services/bedrock/dtos/DesignPackage";
import { ASPECT_CLASS, packageMetaLine } from "@/containers/Coach/PromptStudio/components/PackageCard";

/** The design's slide previews in order, falling back to the package thumbnail. */
export function designSlideUrls(pkg: DesignPackageDetail | undefined): string[] {
  const previews = [...(pkg?.items ?? [])]
    .sort((a, b) => a.position - b.position)
    .map(item => item.activeTemplate?.previewUrl)
    .filter((url): url is string => !!url);
  if (previews.length > 0) return previews;
  return pkg?.thumbnailUrl ? [pkg.thumbnailUrl] : [];
}

/** The slides as the phone preview's media, so it shows the post the way a player would. */
export function designPreviewMedia(pkg: DesignPackageDetail | undefined): Media[] {
  return designSlideUrls(pkg).map((uri, index) => ({
    id: index + 1,
    sortOrder: index,
    category: "IMAGE",
    uri,
    type: "image/png",
    extension: "png",
  }));
}

interface DynamicPromptDesignProps {
  package?: DesignPackageDetail;
  loading: boolean;
  error: boolean;
  onEdit: () => void;
}

/** Sits where Prompt Media does on a static prompt: the design is this prompt's media. */
export const DynamicPromptDesign: React.FC<DynamicPromptDesignProps> = ({ package: pkg, loading, error, onEdit }) => {
  const urls = designSlideUrls(pkg);
  const meta = pkg
    ? [packageMetaLine(pkg), pkg.isCarousel ? `${pkg.slidesCount} ${pluralize("slide", pkg.slidesCount)}` : null]
        .filter(Boolean)
        .join(" · ")
    : null;

  return (
    <Field>
      <Field.Label>Design</Field.Label>
      <HoloBorder active={false} radius="xl" thickness={1}>
        <div className="flex flex-col gap-3 p-3 sm:flex-row sm:items-center">
          <div className="flex gap-2 overflow-x-auto no-scrollbar" data-testid="design-thumbnails">
            {loading ? (
              <Skeleton className="h-20 w-16 rounded-lg" />
            ) : urls.length > 0 ? (
              urls.map((url, index) => (
                <img
                  key={url}
                  src={url}
                  alt={`Slide ${index + 1}`}
                  className={`h-20 w-auto shrink-0 rounded-lg border border-slate-200 object-cover ${
                    pkg ? ASPECT_CLASS[pkg.aspectRatio] : ""
                  }`}
                />
              ))
            ) : (
              <div className="flex h-20 w-20 items-center justify-center rounded-lg bg-slate-50 text-center text-xs text-slate-500">
                {error ? "Preview unavailable" : "No preview yet"}
              </div>
            )}
          </div>
          <div className="flex min-w-0 flex-1 flex-col gap-0.5">
            {pkg && <span className="truncate text-sm font-semibold text-slate-800">{pkg.name}</span>}
            {meta && <span className="text-xs text-slate-500">{meta}</span>}
          </div>
          <Button
            type="button"
            variant="outline"
            className="self-start border-primary text-primary hover:bg-primary/5 hover:text-primary sm:self-center"
            onClick={onEdit}
          >
            Edit in Prompt Studio
            <ArrowRight aria-hidden="true" />
          </Button>
        </div>
      </HoloBorder>
    </Field>
  );
};
